const pad2 = (n) => String(n).padStart(2, '0');

/** Parse YYYY-MM-DD to local Date (noon anchor). */
export function parseIsoYmdToDate(iso) {
  const m = String(iso || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}

function coerceDate(dateStr) {
  if (dateStr instanceof Date) return dateStr;
  if (typeof dateStr === 'string') {
    const iso = parseIsoYmdToDate(dateStr);
    if (iso) return iso;
    return new Date(dateStr);
  }
  return new Date(dateStr);
}

/** Canonical on-screen date: DD/MM/YY (e.g. 26/02/26). */
export function formatDDMMYY(dateStr) {
  const d = coerceDate(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${pad2(d.getFullYear() % 100)}`;
}

/** DD/MM/YYYY when a 4-digit year is required (imports, spreadsheets). */
export function formatDDMMYYYY(dateStr) {
  const d = coerceDate(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Display label for an ISO calendar date (DD/MM/YY). */
export function formatIsoDateDisplay(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return '';
  return formatDDMMYY(iso);
}

/** Display a from/to range in DD/MM/YY. */
export function formatIsoDateRangeDisplay(fromYmd, toYmd) {
  const fromLabel = formatIsoDateDisplay(fromYmd);
  const toLabel = formatIsoDateDisplay(toYmd);
  if (!fromLabel && !toLabel) return '';
  if (!fromLabel) return toLabel;
  if (!toLabel || fromYmd === toYmd) return fromLabel;
  return `${fromLabel} → ${toLabel}`;
}

/** Format date as DD/MM/YY with short day name (e.g. Thu 26/02/26). */
export function formatDDMMYYWithDay(dateStr) {
  const d = coerceDate(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${days[d.getDay()]} ${formatDDMMYY(d)}`;
}

/**
 * Return YYYY-MM-DD for a date in local timezone (avoids UTC shift).
 */
export function toLocalDateString(d) {
  const x = d instanceof Date ? d : new Date(d + (typeof d === 'string' && d.length === 10 && !d.includes('T') ? 'T12:00:00' : ''));
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today's calendar date in browser local TZ as YYYY-MM-DD. */
export function todayLocalIso() {
  return toLocalDateString(new Date());
}

// App is Bulgaria-based: always display backend timestamps in Sofia time, regardless of the viewer's timezone.
const SOFIA_TZ = 'Europe/Sofia';

/**
 * Parse a backend timestamp to a Date. SQLite CURRENT_TIMESTAMP returns a bare, timezone-less UTC string
 * like "2026-06-09 07:01:38"; JS would otherwise read that as browser-local. Treat such bare strings as UTC.
 */
export function parseBackendTimestamp(dateTimeStr) {
  if (dateTimeStr instanceof Date) return dateTimeStr;
  if (typeof dateTimeStr === 'string') {
    const s = dateTimeStr.trim();
    const isBareSql = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(s);
    const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
    if (isBareSql && !hasTz) return new Date(s.replace(' ', 'T') + 'Z');
    return new Date(s);
  }
  return new Date(dateTimeStr);
}

/**
 * Format date and time as DD/MM/YY HH:mm:ss in Sofia time (e.g. 26/02/26 14:30:00).
 */
export function formatDDMMYYDateTime(dateTimeStr) {
  if (!dateTimeStr) return '-';
  const d = parseBackendTimestamp(dateTimeStr);
  if (Number.isNaN(d.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SOFIA_TZ,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(d);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('day')}/${get('month')}/${get('year')} ${hour}:${get('minute')}:${get('second')}`;
}

/**
 * Parse DD/MM/YY or DD/MM/YYYY (or D/M/YY etc.) to YYYY-MM-DD. Returns null if invalid.
 */
export function parseDDMMYYToISO(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  const parts = trimmed.split(/[/\-.]/).map(p => p.trim());
  if (parts.length !== 3) return null;
  let day = parseInt(parts[0], 10);
  let month = parseInt(parts[1], 10);
  let year = parseInt(parts[2], 10);
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null;
  if (year < 100) year += 2000; // 26 -> 2026
  if (year < 1000) return null;
  if (month < 1 || month > 12) return null;
  const lastDay = new Date(year, month, 0).getDate();
  if (day < 1 || day > lastDay) return null;
  const y = String(year);
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** `datetime-local` value (YYYY-MM-DDTHH:mm) for a UTC ms instant in Sofia. */
export function msToSofiaDatetimeLocalValue(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SOFIA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date(n));
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || '00';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

/** Parse `datetime-local` as Sofia wall time → UTC ms. */
export function sofiaWallDatetimeLocalToMs(value) {
  const m = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return NaN;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  const hour = parseInt(m[4], 10);
  const minute = parseInt(m[5], 10);
  if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) return NaN;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: SOFIA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const readParts = (ms) => {
    const parts = dtf.formatToParts(new Date(ms));
    const o = {};
    for (const p of parts) {
      if (p.type !== 'literal') o[p.type] = parseInt(p.value, 10);
    }
    if (o.hour === 24) o.hour = 0;
    return o;
  };
  let t = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 4; i++) {
    const p = readParts(t);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second || 0);
    const want = Date.UTC(year, month - 1, day, hour, minute, 0);
    t -= asUtc - want;
  }
  const pFinal = readParts(t);
  if (
    pFinal.year !== year ||
    pFinal.month !== month ||
    pFinal.day !== day ||
    pFinal.hour !== hour ||
    pFinal.minute !== minute
  ) {
    return NaN;
  }
  // Fall-back ambiguous hour: prefer the earlier UTC instant (first occurrence of repeated wall time).
  const earlier = t - 60 * 60 * 1000;
  const pEarlier = readParts(earlier);
  if (
    pEarlier.year === year &&
    pEarlier.month === month &&
    pEarlier.day === day &&
    pEarlier.hour === hour &&
    pEarlier.minute === minute
  ) {
    return earlier;
  }
  return t;
}

/** Add calendar days to YYYY-MM-DD (local date math). */
function addCalendarDaysYmd(ymd, delta) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** Roster schedule end on calendar ymd → UTC ms (Sofia wall clock). */
export function phmScheduleEndMsForYmd(scheduleTime, ymd) {
  const sm = String(scheduleTime || '').trim().match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
  if (!sm || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ''))) return null;
  const sH = parseInt(sm[1], 10);
  const sM = parseInt(sm[2], 10);
  const eH = parseInt(sm[3], 10);
  const eM = parseInt(sm[4], 10);
  const startMin = sH * 60 + sM;
  const endMin = eH * 60 + eM;
  const overnight = endMin <= startMin;
  const endYmd = overnight ? addCalendarDaysYmd(ymd, 1) : ymd;
  const hhmm = `${String(eH).padStart(2, '0')}:${String(eM).padStart(2, '0')}`;
  return sofiaWallDatetimeLocalToMs(`${endYmd}T${hhmm}`);
}

/** Suggested end ms when closing break / disconnected carry (caps at shift end, not "now" next morning). */
export function suggestPhmIntervalCloseEndedAtMs(row, scheduleTime, nowMs = Date.now()) {
  const startedMs = Number(row && row.started_at) || 0;
  if (!(startedMs > 0)) return nowMs;
  const sk = String((row && row.status_key) || '').toLowerCase();
  const isDisc =
    sk === 'break_disconnected' || sk === 'meeting_training_disconnected';
  if (!isDisc && sk !== 'break') return nowMs;
  const ymd =
    (row && (row.interval_date || row.calendar_date)) ||
    msToSofiaDatetimeLocalValue(startedMs).slice(0, 10);
  const schedEnd = phmScheduleEndMsForYmd(scheduleTime, ymd);
  if (schedEnd != null && schedEnd > startedMs) return Math.min(nowMs, schedEnd);
  if (isDisc && schedEnd != null && startedMs >= schedEnd) return startedMs + 1000;
  return nowMs;
}
